import React from 'react';
import { Link } from 'react-router-dom';
import { Film } from 'lucide-react';

function ThumbnailGrid({ thumbnails }) {
  const count = thumbnails?.length || 0;

  // No thumbnails - show placeholder
  if (count === 0) {
    return (
      <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
        <Film className="w-8 h-8 text-gray-400" />
      </div>
    );
  }

  // 1 thumbnail - single large image
  if (count === 1) {
    return (
      <div className="w-full h-full rounded-lg overflow-hidden">
        <img
          src={thumbnails[0]}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  // 2 thumbnails - side by side
  if (count === 2) {
    return (
      <div className="w-full h-full grid grid-cols-2 gap-0.5 rounded-lg overflow-hidden">
        {thumbnails.map((thumb, i) => (
          <img
            key={i}
            src={thumb}
            alt=""
            className="w-full h-full object-cover"
          />
        ))}
      </div>
    );
  }

  // 3 thumbnails - 2 on top, 1 spanning bottom
  if (count === 3) {
    return (
      <div className="w-full h-full grid grid-cols-2 grid-rows-2 gap-0.5 rounded-lg overflow-hidden">
        <img src={thumbnails[0]} alt="" className="w-full h-full object-cover" />
        <img src={thumbnails[1]} alt="" className="w-full h-full object-cover" />
        <img src={thumbnails[2]} alt="" className="w-full h-full object-cover col-span-2" />
      </div>
    );
  }

  // 4+ thumbnails - 2x2 grid
  return (
    <div className="w-full h-full grid grid-cols-2 grid-rows-2 gap-0.5 rounded-lg overflow-hidden">
      {thumbnails.slice(0, 4).map((thumb, i) => (
        <img
          key={i}
          src={thumb}
          alt=""
          className="w-full h-full object-cover"
        />
      ))}
    </div>
  );
}

function GalleryCard({ gallery, isShared = false }) {
  const galleryUrl = `/gallery/${gallery.urlName || (gallery.name ? gallery.name.replace(/\s+/g, '').toLowerCase() : 'gallery')}`;
  const thumbnails = gallery.previewThumbnails || [];
  const displayName = gallery.name || 'Untitled Gallery';
  const isUntitled = !gallery.name;

  return (
    <li
      className="bg-white border-l-4 border-l-primary-500 border border-gray-200 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow group"
    >
      <Link
        to={galleryUrl}
        className="flex items-stretch p-3 md:p-4 cursor-pointer gap-3 md:gap-4 relative z-10"
      >
        {/* Text content - left side */}
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <div className={`text-base md:text-lg font-semibold mb-0.5 md:mb-1 group-hover:text-primary-500 transition-colors truncate ${isUntitled ? 'italic text-gray-400' : 'text-gray-800'}`}>
            {displayName}
          </div>
          {isShared && gallery.ownerName && (
            <div className="text-xs md:text-sm text-gray-500 mb-0.5 md:mb-1">
              Shared by {gallery.ownerName}
            </div>
          )}
          {gallery.description && (
            <div className="text-gray-600 text-xs md:text-sm mb-1 md:mb-2 line-clamp-1 md:line-clamp-2">
              {gallery.description}
            </div>
          )}
          <div className="flex items-center gap-2 md:gap-3 text-xs text-gray-500 mt-auto">
            {gallery.videoCount !== undefined && (
              <span>{gallery.videoCount} videos</span>
            )}
            {gallery.isPrivate && (
              <span className="px-1.5 md:px-2 py-0.5 bg-gray-100 text-gray-700 rounded font-medium">
                Invite only
              </span>
            )}
            {!gallery.isPrivate && (
              <span className="px-1.5 md:px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">
                Anyone with link
              </span>
            )}
          </div>
        </div>

        {/* Thumbnail grid - responsive sizes */}
        <div className="w-16 h-12 sm:w-24 sm:h-16 md:w-40 md:h-24 flex-shrink-0 self-center">
          <ThumbnailGrid thumbnails={thumbnails} />
        </div>

        {/* Settings icon - far right */}
        {!isShared && (
          <Link
            to={`/manage/${gallery.id}`}
            onClick={(e) => e.stopPropagation()}
            className="p-1.5 md:p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0 self-center group/icon"
            title="Manage gallery settings"
          >
            <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-500 group-hover/icon:text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </Link>
        )}
      </Link>
    </li>
  );
}

export default GalleryCard;
